import api from "./api";
import { resolveCurrentPackage } from "./packageUtils";
import { extractTaxSnapshot, toAmountNumber } from "./taxUtils";

const isObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const toPaginatedCollection = (source, mapper) => {
  const rows = toArray(source).map(mapper);
  if (isObject(source) && !Array.isArray(source)) {
    return {
      ...source,
      rows,
      count: source.count ?? source.total ?? rows.length,
      totalPages:
        source.totalPages ?? source.total_pages ?? (rows.length > 0 ? 1 : 0),
      currentPage: source.currentPage ?? source.current_page ?? 1,
    };
  }
  return {
    rows,
    count: rows.length,
    totalPages: rows.length > 0 ? 1 : 0,
    currentPage: 1,
  };
};

const toGroupedCollection = (source, rowMapper) => {
  if (Array.isArray(source)) {
    return source.map((group, index) => {
      const rows = toArray(
        group?.rows ?? group?.payment_history ?? group?.transactions,
      ).map(rowMapper);
      return {
        ...group,
        groupId:
          group?.company_id ??
          group?.entity_id ??
          group?.id ??
          `group-${index}`,
        companyName:
          group?.company_name ??
          group?.entity_name ??
          group?.companyName ??
          group?.entityName ??
          "Unknown Company",
        companyEmail:
          group?.company_email ??
          group?.entity_email ??
          group?.companyEmail ??
          group?.entityEmail ??
          "",
        rows,
      };
    });
  }

  if (isObject(source)) {
    return Object.entries(source).map(([key, value], index) => {
      const rows = toArray(
        value?.rows ?? value?.payment_history ?? value?.transactions ?? value,
      ).map(rowMapper);
      return {
        ...value,
        groupId:
          value?.company_id ??
          value?.entity_id ??
          value?.id ??
          key ??
          `group-${index}`,
        companyName:
          value?.company_name ??
          value?.entity_name ??
          value?.companyName ??
          value?.entityName ??
          key ??
          "Unknown Company",
        companyEmail:
          value?.company_email ??
          value?.entity_email ??
          value?.companyEmail ??
          value?.entityEmail ??
          "",
        rows,
      };
    });
  }

  return [];
};

export const normalizeInvoice = (invoice = {}) => {
  const invoiceId =
    invoice?.invoice_id ??
    invoice?.invoiceId ??
    invoice?.id ??
    invoice?.postpaid_invoice_id ??
    invoice?.prepaid_invoice_id ??
    null;
  const fallbackAmount =
    invoice?.amount_due ??
    invoice?.due_amount ??
    invoice?.invoice_amount ??
    invoice?.amount ??
    invoice?.total ??
    0;
  const taxSnapshot = extractTaxSnapshot({
    ...invoice,
    amount: invoice?.total_amount ?? fallbackAmount,
  });
  const amount = taxSnapshot.totalAmount || fallbackAmount;
  const platformFee = toAmountNumber(
    invoice?.platform_fee ?? invoice?.platformFee ?? 0,
  );
  const openingBalance = toAmountNumber(
    invoice?.opening_balance ??
      invoice?.previous_balance ??
      invoice?.wallet_opening_balance ??
      invoice?.starting_balance ??
      0,
  );
  const remainingBalanceValue =
    invoice?.remaining_balance ??
    invoice?.balance_after_deduction ??
    invoice?.remainingBalance ??
    invoice?.wallet_balance ??
    null;
  const remainingBalance =
    remainingBalanceValue === null || remainingBalanceValue === undefined
      ? null
      : toAmountNumber(remainingBalanceValue);
  const deductedAmount = toAmountNumber(
    invoice?.deducted_amount ??
      invoice?.total_deducted ??
      invoice?.wallet_deducted_amount ??
      invoice?.paid_amount ??
      0,
  );
  const currencySymbol =
    invoice?.currency_symbol ??
    invoice?.currencySymbol ??
    invoice?.currency_code ??
    invoice?.currencyCode ??
    invoice?.symbol ??
    "";
  return {
    ...invoice,
    invoiceId,
    status:
      invoice?.payment_status ??
      invoice?.status ??
      invoice?.invoice_status ??
      "-",
    amount,
    currencySymbol,
    createdAt:
      invoice?.created_at ??
      invoice?.createdAt ??
      invoice?.invoice_date ??
      invoice?.invoiceDate ??
      null,
    fromDate:
      invoice?.from_date ??
      invoice?.fromDate ??
      invoice?.startdate ??
      invoice?.start_date ??
      null,
    toDate:
      invoice?.to_date ??
      invoice?.toDate ??
      invoice?.enddate ??
      invoice?.end_date ??
      null,
    detailPath:
      invoice?.detail_path ??
      invoice?.detailPath ??
      invoice?.view_path ??
      invoice?.invoice_detail_path ??
      null,
    downloadPath:
      invoice?.download_path ??
      invoice?.downloadPath ??
      invoice?.invoice_download_path ??
      invoice?.invoice_path ??
      null,
    baseAmount: taxSnapshot.baseAmount,
    taxAmount: taxSnapshot.taxAmount,
    totalTaxRate: taxSnapshot.totalTaxRate,
    taxNames: taxSnapshot.taxNamesLabel,
    taxBreakdown: taxSnapshot.taxes,
    totalAmount: taxSnapshot.totalAmount || toAmountNumber(amount),
    dueAmount: toAmountNumber(
      invoice?.due_amount ?? invoice?.amount_due ?? taxSnapshot.totalAmount ?? amount,
    ),
    platformFee,
    netInvoiceAfterPlatformFee: Math.max(
      toAmountNumber(taxSnapshot.totalAmount || amount) - platformFee,
      0,
    ),
    openingBalance,
    deductedAmount,
    remainingBalance,
  };
};

export const normalizePurchaseHistoryItem = (history = {}) => {
  const packageDetails =
    history?.package_details ??
    history?.package ??
    history?.plan_details ??
    history?.plan ??
    {};
  const purchaserDetails =
    history?.purchaser_details ??
    history?.purchaser ??
    history?.user_details ??
    {};
  const paymentDetails =
    history?.payment_details ??
    history?.payment_metadata ??
    history?.metadata ??
    {};
  const cardLast4 =
    history?.card_last4 ??
    paymentDetails?.card_last4 ??
    paymentDetails?.cardLast4 ??
    paymentDetails?.cardNumberMask ??
    null;
  const derivedPurchaserName =
    [
      purchaserDetails?.first_name ?? purchaserDetails?.firstName,
      purchaserDetails?.last_name ?? purchaserDetails?.lastName,
    ]
      .filter(Boolean)
      .join(" ") || null;
  const fallbackAmount =
    history?.amount ??
    history?.paid_amount ??
    history?.total_amount ??
    packageDetails?.amount ??
    0;
  const taxSnapshot = extractTaxSnapshot({
    ...history,
    amount: history?.total_paid ?? history?.total_amount ?? fallbackAmount,
    total_amount:
      history?.total_paid ?? history?.total_amount ?? fallbackAmount,
    total_paid: history?.total_paid ?? history?.total_amount ?? fallbackAmount,
    base_amount:
      history?.base_amount ??
      history?.wallet_credit ??
      history?.wallet_amount ??
      packageDetails?.base_amount ??
      0,
    tax_details:
      history?.tax_details ??
      packageDetails?.taxes ??
      paymentDetails?.tax_details ??
      [],
    tax_names:
      history?.tax_names ??
      history?.tax_label ??
      packageDetails?.tax_names ??
      paymentDetails?.tax_names ??
      "",
    total_tax_rate:
      history?.tax_percentage ??
      history?.total_tax_rate ??
      packageDetails?.tax_percentage ??
      packageDetails?.total_tax_rate ??
      paymentDetails?.tax_percentage ??
      paymentDetails?.total_tax_rate ??
      0,
    tax_amount:
      history?.tax_amount ??
      paymentDetails?.tax_amount ??
      packageDetails?.tax_amount ??
      0,
  });
  return {
    ...history,
    orderId:
      history?.order_id ??
      history?.orderId ??
      history?.merchant_ref ??
      history?.merchantReference ??
      history?.id ??
      "-",
    createdAt:
      history?.created_at ??
      history?.createdAt ??
      history?.payment_date ??
      history?.paymentDate ??
      null,
    packageId:
      history?.package_id ??
      history?.self_service_plan_id ??
      history?.plan_id ??
      packageDetails?.package_id ??
      packageDetails?.id ??
      null,
    packageName:
      history?.package_name ??
      history?.plan_name ??
      packageDetails?.package_name ??
      packageDetails?.name ??
      packageDetails?.plan_name ??
      "-",
    packageType:
      history?.package_type ??
      history?.plan_type ??
      (history?.is_self_service_plan ? "self_service" : null) ??
      packageDetails?.package_type ??
      packageDetails?.plan_type ??
      "-",
    isSelfServicePlan: Boolean(
      history?.is_self_service_plan ??
      history?.self_service_plan_id ??
      packageDetails?.self_service_plan_id,
    ),
    amount:
      taxSnapshot.totalAmount ||
      history?.total_paid ||
      history?.total_amount ||
      fallbackAmount,
    currencySymbol:
      history?.currency_symbol ??
      history?.currencySymbol ??
      history?.currency_code ??
      history?.currencyCode ??
      history?.symbol ??
      "",
    paymentStatus:
      history?.payment_status ??
      history?.status ??
      history?.paymentState ??
      "-",
    entityId: history?.entity_id ?? history?.entityId ?? null,
    purchaserName:
      history?.purchaser_name ??
      derivedPurchaserName ??
      history?.user_name ??
      "-",
    purchaserEmail:
      history?.purchaser_email ??
      purchaserDetails?.email ??
      history?.user_email ??
      null,
    cardBrand:
      history?.card_brand ??
      paymentDetails?.card_brand ??
      paymentDetails?.cardBrand ??
      paymentDetails?.cardType ??
      paymentDetails?.cardScheme ??
      null,
    cardLast4,
    receiptPath:
      history?.receipt_download_path ??
      history?.receiptPath ??
      history?.receipt_download_url ??
      history?.receipt_url ??
      null,
    invoiceDownloadPath:
      history?.invoice_download_path ??
      history?.invoiceDownloadPath ??
      history?.invoice_path ??
      null,
    invoiceId: history?.invoice_id ?? history?.invoiceId ?? null,
    walletTransactionId:
      history?.wallet_transaction_id ?? history?.walletTransactionId ?? null,
    paymentMode:
      history?.payment_method ?? history?.payment_mode ?? history?.mode_of_payment ?? paymentDetails?.paymentMethod ?? null,
    providerTransactionNumber:
      history?.provider_transaction_number ?? history?.gateway_transaction_id ?? history?.transaction_id ?? paymentDetails?.transactionID ?? null,
    transactionType: history?.transaction_type ?? history?.type ?? null,
    featureCost:
      history?.feature_cost ?? history?.base_amount ?? taxSnapshot.baseAmount,
    remainingBalance:
      history?.remaining_balance ??
      history?.balance_after_deduction ??
      history?.remainingBalance ??
      null,
    deductionReason:
      history?.deduction_reason ??
      history?.reason ??
      history?.description ??
      null,
    usedFor:
      history?.used_for ??
      history?.service_name ??
      history?.serviceName ??
      null,
    companyName:
      history?.company_name ??
      history?.entity_name ??
      history?.entityName ??
      null,
    companyEmail:
      history?.company_email ??
      history?.entity_email ??
      history?.entityEmail ??
      null,
    baseAmount: history?.base_amount ?? taxSnapshot.baseAmount,
    taxAmount: history?.tax_amount ?? taxSnapshot.taxAmount,
    totalTaxRate:
      history?.tax_percentage ??
      history?.total_tax_rate ??
      taxSnapshot.totalTaxRate,
    taxNames:
      history?.tax_names ?? history?.tax_label ?? taxSnapshot.taxNamesLabel,
    taxBreakdown: history?.tax_details ?? taxSnapshot.taxes,
    totalAmount:
      history?.total_paid ?? history?.total_amount ?? taxSnapshot.totalAmount,
    walletCredit:
      history?.wallet_credit ?? history?.base_amount ?? taxSnapshot.baseAmount,
    platformFee: Number(history?.platform_fee ?? history?.platformFee ?? 0),
  };
};

export const isPlanPurchaseHistory = (history = {}) => {
  const sourceText = [
    history?.source,
    history?.transactionType,
    history?.transaction_type,
    history?.type,
    history?.usedFor,
    history?.used_for,
    history?.reason,
    history?.deductionReason,
    history?.description,
    history?.packageType,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (
    sourceText.includes("plan purchase") ||
    sourceText.includes("package purchase") ||
    sourceText.includes("plan_payment") ||
    sourceText.includes("package_payment") ||
    sourceText.includes("subscription purchase")
  ) {
    return true;
  }

  return Boolean(
    history?.isPlanPurchase ||
      history?.is_plan_purchase ||
      history?.isSelfServicePlanPurchase ||
      history?.is_self_service_plan_purchase,
  );
};

export const normalizeEntity = (entity = {}) => {
  const packageType =
    entity?.package_type ??
    entity?.packageType ??
    entity?.plan_type ??
    entity?.planType ??
    "-";
  const currencySymbol =
    entity?.currency_symbol ??
    entity?.currencySymbol ??
    entity?.prepaid_currency ??
    entity?.postpaid_currency ??
    entity?.currency_code ??
    entity?.currencyCode ??
    "";
  return {
    ...entity,
    entityId: entity?.entity_id ?? entity?.entityId ?? entity?.id ?? null,
    entityName:
      entity?.entity_name ?? entity?.entityName ?? entity?.name ?? "-",
    packageType,
    currentBalance:
      entity?.current_balance ??
      entity?.wallet_balance ??
      entity?.walletBalance ??
      entity?.balance ??
      0,
    currentInvoice:
      entity?.current_invoice ??
      entity?.invoice_amount ??
      entity?.due_amount ??
      entity?.amount_due ??
      0,
    currencySymbol,
    lastPaymentDate:
      entity?.date_of_last_payment ??
      entity?.last_payment_date ??
      entity?.lastPaymentDate ??
      entity?.updated_at ??
      null,
    lastInvoiceDate:
      entity?.date_of_last_invoice ??
      entity?.last_invoice_date ??
      entity?.lastInvoiceDate ??
      entity?.created_at ??
      null,
    invoiceId:
      entity?.invoice_id ??
      entity?.invoiceId ??
      entity?.latest_invoice_id ??
      entity?.current_invoice_id ??
      null,
    // Enterprise billing list fields (from fetchAllEntityBillingList)
    enterpriseAdminName: entity?.enterprise_admin_name ?? null,
    enterpriseAdminEmail: entity?.enterprise_admin_email ?? null,
    totalUsageAmount: Number(entity?.total_usage_amount ?? 0),
    // billing_summary: { kyc_check: { total_amount, total_count, services[] }, aml_check: {...}, ... }
    billingSummary: entity?.billing_summary ?? null,
  };
};

export const normalizeSelectedEntity = (selectedEntity = {}) => {
  const invoicesSource =
    selectedEntity?.invoices ?? selectedEntity?.invoice_list ?? [];
  const wallet = selectedEntity?.wallet ?? {};
  const currentPlanSource =
    selectedEntity?.current_plan ??
    selectedEntity?.active_plan ??
    selectedEntity?.plan ??
    {};
  const resolvedCurrentPlan = resolveCurrentPackage(currentPlanSource);

  return {
    ...selectedEntity,
    entityId:
      selectedEntity?.entity_id ??
      selectedEntity?.entityId ??
      selectedEntity?.id ??
      null,
    entityName:
      selectedEntity?.entity_name ??
      selectedEntity?.entityName ??
      selectedEntity?.name ??
      "-",
    businessType:
      selectedEntity?.business_type ?? selectedEntity?.businessType ?? "-",
    walletBalance:
      selectedEntity?.wallet_balance ??
      selectedEntity?.walletBalance ??
      selectedEntity?.balance ??
      wallet?.current_balance ??
      0,
    currencySymbol:
      selectedEntity?.currency_symbol ??
      selectedEntity?.currencySymbol ??
      selectedEntity?.currency_code ??
      selectedEntity?.currencyCode ??
      "",
    // Wallet block with transactions URL for quick navigation
    wallet: {
      currentBalance: wallet?.current_balance ?? 0,
      lastTopupAt: wallet?.last_topup_at ?? null,
      lastPlanPurchaseAt: wallet?.last_plan_purchase_at ?? null,
      walletTransactionsUrl: wallet?.wallet_transactions_url ?? null,
    },
    // Current package plan with service pricing breakdown
    currentPlan: {
      ...currentPlanSource,
      ...resolvedCurrentPlan,
      packageId: resolvedCurrentPlan?.package_id ?? null,
      entityPackageId: resolvedCurrentPlan?.entity_package_id ?? null,
      packageName: resolvedCurrentPlan?.package_name ?? "-",
      packageType: resolvedCurrentPlan?.package_type ?? "-",
      packageAmount:
        resolvedCurrentPlan?.package_amount ??
        resolvedCurrentPlan?.total_amount ??
        0,
      amountUsed:
        currentPlanSource?.amount_used ?? currentPlanSource?.amountUsed ?? 0,
      remainingAmount:
        currentPlanSource?.remaining_amount ??
        currentPlanSource?.remainingAmount ??
        resolvedCurrentPlan?.remaining_amount ??
        resolvedCurrentPlan?.remainingAmount ??
        0,
      expiryDate:
        currentPlanSource?.expiry_date ?? currentPlanSource?.expiryDate ?? null,
      packageService:
        resolvedCurrentPlan?.services ??
        currentPlanSource?.package_service ??
        {},
      // Service prices grouped by category: { kyc_check: [{service_key, service_name, price_per_use}], ... }
      servicePricing:
        currentPlanSource?.service_pricing ??
        resolvedCurrentPlan?.services ??
        {},
      // Explicit manual cleaning / outsourced check price per use
      manualCleaningPrice:
        currentPlanSource?.manual_cleaning_price ??
        currentPlanSource?.manualCleaningPrice ??
        null,
      taxAmount: resolvedCurrentPlan?.tax_amount ?? 0,
      totalTaxRate: resolvedCurrentPlan?.total_tax_rate ?? 0,
      taxNames: resolvedCurrentPlan?.tax_names ?? "",
      isSelfServicePlan: Boolean(
        resolvedCurrentPlan?.is_self_service_plan ??
        resolvedCurrentPlan?.self_service_plan_id,
      ),
    },
    billingSettings:
      selectedEntity?.billing_settings ?? selectedEntity?.billingSettings ?? {},
    people: toArray(
      selectedEntity?.people ?? selectedEntity?.people_details,
    ).filter(Boolean),
    invoices: toPaginatedCollection(invoicesSource, normalizeInvoice),
  };
};

/**
 * Normalize a row from GET /api/wallet/getAllEntityWalletBalances (superadmin view)
 */
export const normalizeWalletEntity = (row = {}) => {
  return {
    ...row,
    entityId: row?.entity_id ?? null,
    entityName: row?.entity_name ?? "-",
    entityEmail: row?.entity_email ?? null,
    walletBalance: parseFloat(row?.wallet_balance ?? 0),
    maxCreditLimit: parseFloat(row?.max_credit_limit ?? 0),
    enterpriseAdminName: row?.enterprise_admin_name ?? null,
    enterpriseAdminEmail: row?.enterprise_admin_email ?? null,
    packageType: row?.package_type ?? "-",
    packageName: row?.package_name ?? "-",
    packageAmount: parseFloat(row?.package_amount ?? 0),
    expiryDate: row?.expiry_date ?? null,
    totalServiceUsageAmount: parseFloat(row?.total_service_usage_amount ?? 0),
    totalServiceUsageCount: parseInt(row?.total_service_usage_count ?? 0),
    lastDebitAt: row?.last_debit_at ?? null,
    totalTopupAmount: parseFloat(row?.total_topup_amount ?? 0),
    totalTopupCount: parseInt(row?.total_topup_count ?? 0),
    lastCreditAt: row?.last_credit_at ?? null,
    packageService: row?.package_service ?? {},
    currencyCode: row?.currency_code || row?.billing_currency_code || "",
    currencySymbol: row?.currency_symbol || "",
  };
};

export const normalizeBillingSidebarData = (payload = {}) => {
  const rawData = payload?.data ?? payload;
  const entitiesSource =
    rawData?.entities ??
    rawData?.entity_list ??
    rawData?.billing_entities ??
    [];
  const purchaseHistorySource =
    rawData?.purchase_history ??
    rawData?.purchaseHistory ??
    rawData?.payment_history ??
    [];
  const groupedPurchaseHistorySource =
    purchaseHistorySource?.grouped_rows ??
    purchaseHistorySource?.groupedRows ??
    rawData?.grouped_rows ??
    rawData?.grouped_by_company ??
    rawData?.grouped_payment_history ??
    [];
  const selectedEntitySource =
    rawData?.selected_entity ?? rawData?.selectedEntity ?? null;
  const normalizedPurchaseHistory = toPaginatedCollection(
    purchaseHistorySource,
    normalizePurchaseHistoryItem,
  );
  const summarySource =
    rawData?.purchase_history?.summary ??
    purchaseHistorySource?.summary ??
    rawData?.summary ??
    {};

  return {
    raw: rawData,
    entities: toPaginatedCollection(entitiesSource, normalizeEntity),
    purchaseHistory: {
      ...normalizedPurchaseHistory,
      groupedRows: toGroupedCollection(
        groupedPurchaseHistorySource,
        normalizePurchaseHistoryItem,
      ),
      summary: {
        successCount: Number(
          summarySource?.successCount ??
            summarySource?.success_count ??
            summarySource?.count ??
            0,
        ),
        totalTax: Number(
          summarySource?.totalTax ?? summarySource?.total_tax ?? 0,
        ),
        totalDeducted: Number(
          summarySource?.totalDeducted ?? summarySource?.total_deducted ?? 0,
        ),
        currencyCode:
          summarySource?.currencyCode ??
          summarySource?.currency_code ??
          "",
        currencySymbol:
          summarySource?.currencySymbol ??
          summarySource?.currency_symbol ??
          "",
      },
    },
    selectedEntity: selectedEntitySource
      ? normalizeSelectedEntity(selectedEntitySource)
      : null,
  };
};

export const resolveApiPath = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const baseUrl = (api?.defaults?.baseURL ?? "").replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};
